import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import cn from 'classnames';
import slugify from 'slugify';
import _ from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faTimes } from '@fortawesome/pro-light-svg-icons';
import { faSearch } from '@fortawesome/pro-regular-svg-icons';
import './ShopTabs.scss';

import { hasProductIdsPromoted, isShopper, getSections } from '../../../Helpers/user_helpers';
import { setUrlParam, removeUrlParam } from '../../../Helpers/helpers';
import { getFormattedSectionTitle } from '../../../Helpers/formatting';

import ShopMySelect from '../../General/ShopMySelect';
import ShopSectionsModal from '../../Sections/ShopSectionsModal';
import Tooltip from '../../General/Tooltip';

const ShopTabs = props => {
  const { user, isYourShop, activeRefinementObjects } = props;
  const { activeCurator, activeCuratorGroup, activeBrands, activeCategory, activeDepartment, activeSections } = activeRefinementObjects || {};

  // Edit Sections
  const [isSectionsModalVisible, setIsSectionsModalVisible] = React.useState(false);
  const closeSectionsModal = () => setIsSectionsModalVisible(false);
  const openSectionsModal = () => setIsSectionsModalVisible(true);

  // Tabs
  const tabs = [
    {
      label: 'Latest Finds',
      mobileLabel: 'Latest',
      onClick: () => props.goToTab('latest'),
      isActive: props.tab === 'latest',
      isHidden: !!props.query
    },
    {
      label: 'Most Popular',
      mobileLabel: 'Popular',
      onClick: () => props.goToTab('popular'),
      isActive: props.tab === 'popular',
      isHidden: (isShopper(user) && isYourShop) || !!props.query
    },
    ...(isYourShop
      ? [
          ...getSections(user).map((section, idx) => ({
            value: section.id,
            label: getFormattedSectionTitle(section),
            onClick: () => props.goToSectionId(section.id),
            // Root Collections Tab

            isActive:
              props.Section_id === section.id || // Direct Match
              (!!section.title && slugify(section.title).toLowerCase() === props.Section_title) || // Title Match
              (props.tab === 'collections' && !props.Section_id && !props.Section_title && idx === 0),
            tooltipLabel: section.isHidden && !isShopper(user) ? 'This section is currently hidden.' : null,
            isHiddenSection: section.isHidden && !isShopper(user),
            isHidden: !!props.query
          })),
          {
            value: 'archive',
            label: 'Archive',
            onClick: () => props.goToArchivedCollections(),
            isActive: props.showArchivedCollections,
            isHidden: isShopper(user) || !!props.query
          },
          {
            value: 'edit-sections',
            label: 'Edit',
            onClick: openSectionsModal,
            isActive: props.isEditingShopData,
            isEditBtn: true,
            isHidden: isShopper(user) || !!props.query
          }
        ]
      : props.isCuratorShop
      ? activeSections?.map(section => ({
          value: section.id,
          label: getFormattedSectionTitle(section),
          onClick: () => props.goToSectionId(section.id),
          isActive: props.Section_id === section.id,
          isHidden: !props.isCuratorShop || !!props.query // Only show for curator shops
        })) || []
      : props.isCuratorGroupShop
      ? activeSections?.map(section => ({
          value: section.type,
          label: section.title,
          onClick: () => props.goToSectionType(section.type),
          isActive: props.sectionType === section.type,
          isHidden: !props.isCuratorGroupShop || !!props.query, // Only show for curator groups
          isNew: section.isNew
        })) || []
      : []),
    {
      value: 'consults',
      label: 'Consults',
      onClick: () => props.goToTab('consults'),
      isActive: props.tab === 'consults',
      mobileLabel: 'Consults',
      sublabel: '1 on 1 consultations.',
      isHidden: !activeCurator || !activeCurator.showConsultsTabInShop || !!props.query
    },
    {
      label: 'For You',
      mobileLabel: 'For You',
      onClick: () => props.goToTab('foryou'),
      isActive: props.tab === 'foryou',
      isHidden: isYourShop || !!props.query,
      isDisabled: !hasProductIdsPromoted(user),
      isDisabledWarning: !hasProductIdsPromoted(user) ? 'Heart a few products to get personalized recommendations' : null
    }
  ].filter(c => !c.isHidden);

  // Search Behavior
  const [searchQuery, setSearchQuery] = React.useState('');
  const [isInSearchMode, setIsInSearchMode] = React.useState(false);
  const toggleSearchMode = () => {
    // If we have a query we want to set the input value to the query
    if (props.query) setSearchQuery(props.query);
    setIsInSearchMode(!isInSearchMode);
  };
  const clearSearch = () => {
    setSearchQuery('');
    setIsInSearchMode(false);
    removeUrlParam('query');
  };
  const clearGlobalSearch = () => {
    clearSearch();
    props.setQuery('');
    removeUrlParam('query');
  };
  const blurSearch = () => !searchQuery && setIsInSearchMode(false);
  const closeSearchMode = () => setIsInSearchMode(false);

  const submitSearch = e => {
    e?.preventDefault();
    props.setQuery(searchQuery);
    setUrlParam('query', searchQuery);
    clearSearch();

    // CLear other results
    props.clearCategoryId();
    props.clearDepartmentId();
    props.clearIndustryId();
    props.clearBrandIds();
    props.goToTab('popular');

    // Scroll to top of the results
    setTimeout(() => {
      const rect = props.shopTabsRef.current.getBoundingClientRect();
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      window.scrollTo({
        top: rect.top + scrollTop - 44, // Offset for the header
        behavior: 'smooth'
      });
    }, 200);
  };

  // Dynamic UI Handling
  const leftAlignRef = React.useRef(null);
  const rightAlignRef = React.useRef(null);
  const [leftAlignTabs, setLeftAlignTabs] = React.useState(false);
  const tabDisplayHash = tabs.map(t => t.label).join(',');
  React.useEffect(() => {
    const checkTabAlignment = () => {
      // Check the position the tabs to see if we need it to be left aligned
      if (leftAlignRef.current) {
        const firstTabRect = leftAlignRef.current.getBoundingClientRect();
        const isFirstTabOffScreen = firstTabRect.x < 0;
        const lastTabRect = rightAlignRef.current.getBoundingClientRect();
        const isLastTabOffScreen = lastTabRect.x > window.innerWidth;
        if (isFirstTabOffScreen && !leftAlignTabs) {
          setLeftAlignTabs(true);
        } else if (!isLastTabOffScreen && leftAlignTabs) {
          setLeftAlignTabs(false);
        }
      }
    };

    // Check alignment on mount and when dependencies change
    checkTabAlignment();

    // Add resize listener
    window.addEventListener('resize', checkTabAlignment);

    // Cleanup
    return () => window.removeEventListener('resize', checkTabAlignment);
  }, [tabDisplayHash, props.tab, leftAlignTabs]);

  // UI Handling
  const additionalClasses = {
    'in-search-mode': isInSearchMode,
    'left-align-tabs': leftAlignTabs
  };

  return (
    <>
      <div ref={props.shopTabsRef} className='shop-tabs-outer-container'>
        <div className={cn('shop-tabs', additionalClasses)}>
          <div ref={leftAlignRef} />
          <div className='search-container'>
            {isInSearchMode ? (
              <div className='is-searching-container-outer'>
                <form onSubmit={submitSearch} className='is-searching-container'>
                  <div className='search-input-container has-actions'>
                    <div className='search-icon'>
                      <FontAwesomeIcon icon={faSearch} />
                    </div>
                    <input
                      autoFocus
                      onBlur={blurSearch} // REMOVE BEFORE LANDING
                      placeholder={
                        activeCurator
                          ? `Search ${activeCurator.name}${_.last(activeCurator.name) === 's' ? "'" : "'s"} Shop`
                          : activeCuratorGroup
                          ? `Search ${activeCuratorGroup.title}${_.last(activeCuratorGroup.title) === 's' ? "'" : "'s"} Finds`
                          : activeBrands?.length
                          ? `Search ${activeBrands[0].name}`
                          : activeCategory
                          ? `Search ${activeCategory.name}`
                          : activeDepartment
                          ? `Search ${activeDepartment.name}`
                          : 'Search Shop'
                      }
                      type='text'
                      value={searchQuery}
                      onChange={e => setSearchQuery(e.target.value)}
                    />
                    <div className={cn('submit-icon', { active: !!searchQuery })} onClick={submitSearch}>
                      <FontAwesomeIcon icon={faArrowRight} />
                    </div>
                  </div>
                  <div className={cn('clear-icon')} onClick={closeSearchMode}>
                    <FontAwesomeIcon icon={faTimes} />
                  </div>
                </form>
              </div>
            ) : (
              !props.query && (
                <div onClick={toggleSearchMode} className={cn('search-button', { 'search-again': !!props.query })}>
                  <FontAwesomeIcon icon={faSearch} />
                </div>
              )
            )}
          </div>
          {props.query && !isInSearchMode && (
            <>
              <div className='shop-tab active clearable'>
                <div onClick={toggleSearchMode}>{props.query}</div>
                <div onClick={toggleSearchMode} className='clear-search-icon'>
                  <FontAwesomeIcon icon={faSearch} />
                </div>
                <div className='clear-search-label' onClick={clearGlobalSearch}>
                  Clear Search
                  <FontAwesomeIcon icon={faTimes} />
                </div>
              </div>
            </>
          )}
          {!isInSearchMode &&
            tabs.map((tab, idx) => {
              const click = () => (tab.isActive ? null : tab.isDisabled ? window.ALERT.warn(tab.isDisabledWarning) : tab.onClick());
              const additionalClasses = {
                active: tab.isActive,
                'has-new-label': tab.isNew,
                'is-edit-btn': tab.isEditBtn,
                'is-hidden-section': tab.isHiddenSection
              };

              return (
                <Tooltip key={tab.label + idx} message={tab.tooltipLabel}>
                  <div className={cn('shop-tab', additionalClasses)} onClick={click}>
                    {tab.label}
                    {tab.isNew && <div className='new-label'>New</div>}
                  </div>
                </Tooltip>
              );
            })}
          <div ref={rightAlignRef} />
        </div>
        {window.__ADMIN_CONTROL_MODE__ && (
          <div className='admin-control-mode-container'>
            <div className='admin-control-mode-select-container'>
              <ShopMySelect
                disableSearch
                isDark
                options={[
                  { value: 'default', label: 'Default Images', sublabel: 'Standard Combination' },
                  { value: 'preview', label: 'Preview Only', sublabel: 'Products on White Backgrounds for Search Results' },
                  { value: 'cover', label: 'Primary Only', sublabel: 'Full Cover Images for Product Landing Pages' }
                ]}
                value={props.imageStyleEnum}
                placeholder='Image Style'
                onChangeValue={newValue => props.setImageStyleEnum(newValue)}
                isSearching={false}
                isDisabled={false}
                isLoading={false}
              />
            </div>
          </div>
        )}
      </div>

      {isSectionsModalVisible && <ShopSectionsModal close={closeSectionsModal} sectionsUser={props.user} />}
    </>
  );
};

ShopTabs.propTypes = {
  user: PropTypes.object.isRequired,
  isYourShop: PropTypes.bool.isRequired,
  activeRefinementObjects: PropTypes.object.isRequired,
  goToTab: PropTypes.func.isRequired,
  tab: PropTypes.string.isRequired,
  imageStyleEnum: PropTypes.string.isRequired,
  setImageStyleEnum: PropTypes.func.isRequired,
  shopTabsRef: PropTypes.object.isRequired
};

const mapStateToProps = state => {
  const { user } = state;
  return { user };
};

export default connect(mapStateToProps, {
  // functions go here
})(ShopTabs);
